import React, { useState, useCallback, useEffect } from 'react';
import { MessageCircle, Volume2, VolumeX } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { useExpertChat } from '../contexts/ExpertChatContext';
import { useAuth } from '../contexts/AuthContext';
import { useExpertStream } from '../hooks/useExpertStream';
import { useActiveSpeaker } from '../hooks/useActiveSpeaker';
import { useAvatarState } from '../hooks/useAvatarState';
import { expertApi } from '../services/expertApi';
import ConversationList from '../components/expert/ConversationList';
import MessageList from '../components/expert/MessageList';
import ChatInput from '../components/expert/ChatInput';
import AvatarOverlay from '../components/avatar/AvatarOverlay';
import PersonaSelector from '../components/chat/PersonaSelector';
import { PermissionGate } from '../components/PermissionGate';
import { UpgradeNudge } from '../components/UpgradeNudge';

const ExpertChat: React.FC = () => {
  const { activeConversationId, setConversation, closeDrawer, voiceModeEnabled, setVoiceModeEnabled, selectedPersonaSlug, setSelectedPersonaSlug } = useExpertChat();
  const { isAuthenticated } = useAuth();
  const activeSpeaker = useActiveSpeaker();

  // H1 fix: fetch banker preferences to read banker_voice_autoplay
  const { data: bankerPrefs } = useQuery({
    queryKey: ['banker-preferences'],
    queryFn: () => expertApi.getBankerPreferences(),
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  const { messages, streamingMessage, isStreaming, error, sendMessage, loadConversation, clearMessages } = useExpertStream({
    tts: activeSpeaker,
    voiceMode: voiceModeEnabled,
    bankerAutoplay: bankerPrefs?.banker_voice_autoplay ?? false,
    bankerVoice: bankerPrefs?.banker_advisor_voice ?? undefined,
    analystVoice: bankerPrefs?.banker_analyst_voice ?? undefined,
    personaSlug: selectedPersonaSlug,
  });
  const [showConvList, setShowConvList] = useState(true);

  // Avatar state (Story 7.4)
  const { avatarState, isVisible, setIsVisible } = useAvatarState({
    isQueuePlaying: activeSpeaker.isQueuePlaying,
    isPlaying: activeSpeaker.isPlaying,
    isStreaming,
  });

  const avatarEnabled = bankerPrefs?.banker_avatar_enabled ?? false;
  const avatarPersona = selectedPersonaSlug;
  const avatarPersonaName = selectedPersonaSlug === 'banker'
    ? (bankerPrefs?.banker_advisor_name ?? 'Investment Banker')
    : selectedPersonaSlug === 'copilot'
    ? 'Market Copilot'
    : (bankerPrefs?.banker_analyst_name ?? 'Market Analyst');

  // H1 fix: track convergence from banker-notification events (AC3 badge)
  const [isConvergence, setIsConvergence] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ isConvergence?: boolean }>).detail;
      setIsConvergence(detail?.isConvergence ?? false);
    };
    window.addEventListener('banker-notification', handler);
    return () => window.removeEventListener('banker-notification', handler);
  }, []);

  // Close drawer when full-page view opens
  useEffect(() => {
    closeDrawer();
  }, [closeDrawer]);

  // Load conversation when activeConversationId changes
  useEffect(() => {
    if (activeConversationId) {
      loadConversation(activeConversationId);
      setShowConvList(false);
    }
  }, [activeConversationId, loadConversation]);

  const handleSend = useCallback((message: string) => {
    sendMessage(message, activeConversationId ?? undefined);
    setShowConvList(false);
  }, [sendMessage, activeConversationId]);

  const handleNewChat = useCallback(() => {
    setConversation(null);
    clearMessages();
    setShowConvList(false);
  }, [setConversation, clearMessages]);

  const handleSelectConversation = useCallback((id: number) => {
    setConversation(id);
    setShowConvList(false);
  }, [setConversation]);

  const toggleVoiceMode = useCallback(() => {
    if (voiceModeEnabled) {
      activeSpeaker.stop();
      toast('Voice mode off');
    } else {
      toast('Voice mode on');
    }
    setVoiceModeEnabled(!voiceModeEnabled);
  }, [voiceModeEnabled, setVoiceModeEnabled, activeSpeaker]);

  return (
    <PermissionGate
      permission="feature:expert-chat"
      fallback={<div className="p-8"><UpgradeNudge tier="pro" features={['AI-powered investment analysis', '50 messages per day', 'Strategy library access', 'Voice interaction mode']} /></div>}
    >
    <>
    {/* Avatar overlay — 3D for feature:banker-avatar-3d users, 2D fallback (AC4) */}
    {avatarEnabled && (
      <PermissionGate
        permission="feature:banker-avatar-3d"
        fallback={
          <PermissionGate permission="feature:banker-avatar-2d">
            <AvatarOverlay
              variant="2d"
              personaName={avatarPersonaName}
              persona={avatarPersona}
              avatarState={avatarState}
              isVisible={isVisible}
              onClose={() => setIsVisible(false)}
              isConvergence={isConvergence}
            />
          </PermissionGate>
        }
      >
        <AvatarOverlay
          variant="3d"
          personaName={avatarPersonaName}
          persona={avatarPersona}
          avatarState={avatarState}
          isVisible={isVisible}
          onClose={() => setIsVisible(false)}
          audioRef={activeSpeaker.audioRef}
          isConvergence={isConvergence}
        />
      </PermissionGate>
    )}
    <div className="flex h-[calc(100vh-73px-5rem)] lg:h-[calc(100vh-73px)]">
      {/* Left panel — conversation list */}
      <div className={`w-72 bg-slate-800 border-r border-slate-700 flex-shrink-0 ${showConvList ? '' : 'hidden lg:flex lg:flex-col'}`}>
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <MessageCircle size={20} className="text-teal-400" />
            <h1 className="text-lg font-medium text-slate-100">Expert Chat</h1>
          </div>
        </div>
        <ConversationList
          activeId={activeConversationId}
          onSelect={handleSelectConversation}
          onNewChat={handleNewChat}
        />
      </div>

      {/* Right panel — messages */}
      <div className={`flex-1 flex flex-col bg-slate-900 ${showConvList ? 'hidden lg:flex' : ''}`}>
        {/* Header bar with voice toggle */}
        <div className="border-b border-slate-700 px-4 py-2 flex items-center justify-between">
          <button
            onClick={() => setShowConvList(true)}
            className="lg:hidden text-sm text-teal-400 hover:text-teal-300 py-2 min-h-[44px] flex items-center"
          >
            &larr; Conversations
          </button>
          <div className="hidden lg:block" />
          <button
            onClick={toggleVoiceMode}
            className={`p-1.5 rounded transition-colors ${voiceModeEnabled ? 'text-teal-400 hover:text-teal-300' : 'text-slate-400 hover:text-slate-200'}`}
            title={voiceModeEnabled ? 'Voice mode on' : 'Voice mode off'}
          >
            {voiceModeEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
        </div>

        <MessageList messages={messages} streamingMessage={streamingMessage} />

        {error && (
          <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/30">
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        <PersonaSelector
          selectedSlug={selectedPersonaSlug}
          onSelect={setSelectedPersonaSlug}
        />
        <ChatInput onSend={handleSend} disabled={isStreaming} />
      </div>
    </div>
    </>
    </PermissionGate>
  );
};

export default ExpertChat;
